import { api } from "jiffy-api";
import { EventBus, Events } from "jiffy-store";
import pageMode from "components/jiffy/vue-product-details-print/utils/page-mode";
import { generateTimeRemainingText } from "common/utils/ui/generate-time-remaining";
import initProductCompositionCountdown from "components/jiffy/products/product-composition-countdown";
import { moneyFormat } from "common/utils/filters/money";
import selectors from "./selectors";

const $getActiveSlide = () => document.querySelector(selectors.global.ACTIVE_COLOR_SLIDE);
const $getCutoff = () => document.querySelectorAll(selectors.made.CUTOFF);
const $getDeliveryDates = () => document.querySelector(selectors.global.DELIVERY_DATES);
const $getDirectDeliveryDates = () =>
  document.querySelectorAll(selectors.made.DIRECT_DELIVERY_DATE);
const $getModalDeliveryDates = () => document.querySelector(selectors.global.DELIVERY_DATES);
const $getModalShipmentList = () => document.querySelector(selectors.made.MODAL_SHIPMENTS_LIST);
const $getModalSpinner = () => document.querySelector(selectors.global.MODAL_SPINNER);
const $getSpinners = () => document.querySelectorAll(selectors.made.SPINNER);
const $getWrappers = () => document.querySelectorAll(selectors.made.WRAPPER);
const $getFreeShippingThreshold = () =>
  document.querySelector(selectors.made.FREE_SHIPPING_THRESHOLD);

let timersTimeouts = [];
const clearTimers = () => {
  timersTimeouts.forEach(timer => clearTimeout(timer));
  timersTimeouts = [];
};

const makeTimer = ($cutoffNode, cutoff) => {
  const $cutoff = $cutoffNode;

  const updateTimer = () => {
    const curTime = Date.now();
    const diff = Math.floor((cutoff - curTime) / 1000);

    $cutoff.innerHTML = generateTimeRemainingText(cutoff);
    $cutoff.dataset.timeLeftInMinutes = Math.floor(diff / 60);
    timersTimeouts.push(setTimeout(updateTimer, 60_000)); // One time per minute
  };

  updateTimer();
};

const addHighLight = $element => {
  $element.classList.add(selectors.global.DATE_HIGHLIGHT);
  setTimeout(() => {
    $element.classList.remove(selectors.global.DATE_HIGHLIGHT);
  }, 500);
};

const insertDate = (dates, $dateNode) => {
  const $date = $dateNode;
  const [singleDate] = dates.value_full.split(" & ");
  $date.innerHTML = singleDate;

  if (dates.shipments) {
    const deliveryDate = new Date(Math.min(...dates.shipments.map(s => new Date(s.full_date))))
      .toISOString()
      .slice(0, 10);
    $date.dataset.deliveryDate = deliveryDate;
  }

  addHighLight($date);
};

const insertDeliveryDates = ({ cart_dates: cartDates, composition_dates: compositionDates }) => {
  $getDirectDeliveryDates().forEach(insertDate.bind(this, compositionDates));

  const shipmentsForModal = cartDates.shipments || compositionDates.shipments;
  if (shipmentsForModal) {
    $getDeliveryDates()?.classList?.remove("hidden");
    $getModalShipmentList().innerHTML = shipmentsForModal
      .map(({ date }) => `<div class="continue-shopping-modal__delivery-dates-item">${date}</div>`)
      .join("");
  }
};

const updateHTML = deliveryDates => {
  const { cart_dates: cartDates, composition_dates: compositionDates } = deliveryDates;
  if (!cartDates?.timeleft && !compositionDates?.timeleft) return;

  const cutoffSource =
    cartDates.shipments?.length > 0 ? cartDates.timeleft : compositionDates.timeleft;
  const getCutoffTime = new Date(cutoffSource).getTime();
  const shippingCostInfo = compositionDates.shipping_cost_info;

  insertDeliveryDates(deliveryDates);

  clearTimers();
  $getCutoff().forEach($cutoff => {
    if ($cutoff.dataset.countdownMode === "short") {
      $cutoff.dataset.countdown = cutoffSource;
    } else {
      makeTimer($cutoff, getCutoffTime);
    }
  });

  initProductCompositionCountdown();

  updateFreeShippingThreshold(shippingCostInfo);
};

const setPDPDatesLoading = isLoading => {
  $getSpinners()?.forEach($spinner => $spinner?.classList.toggle("shown", isLoading));
  $getWrappers()?.forEach($wrapper => $wrapper?.classList.remove("hidden"));

  if (pageMode.isMadeMode) {
    $getModalDeliveryDates()?.classList?.toggle("hidden", isLoading);
    $getModalSpinner()?.classList?.toggle("continue-shopping-modal__loading--visible", isLoading);
  }
};

const getDeliveryDateForMade = async () => {
  const { compositionId: sliderCompositionId, supportsMade } = $getActiveSlide()?.dataset || {};
  const compositionId = sliderCompositionId || window.jiffy_made_product_props?.composition_id;
  if (!compositionId || supportsMade === "false") return [{}, 0];

  const data = await api.print.estimatedDelivery({
    composition_id: compositionId
  });

  if (data.success) {
    return data;
  }

  return {};
};

const loadDeliveryEstimates = async (useLoader = true) => {
  useLoader && setPDPDatesLoading(true);

  const deliveryDates = await getDeliveryDateForMade();

  useLoader && setPDPDatesLoading(false);
  updateHTML(deliveryDates);

  return deliveryDates;
};

const updateFreeShippingThreshold = shippingCostInfo => {
  if (!shippingCostInfo) return;

  const $freeShippingElement = $getFreeShippingThreshold();
  if ($freeShippingElement) {
    $freeShippingElement.textContent = `${moneyFormat(shippingCostInfo.user_free_shipping_threshold)}`;
  }
};

const initMadeDates = () => {
  loadDeliveryEstimates();

  EventBus.on(Events.PRODUCT_COLOR_SELECTED, () => loadDeliveryEstimates(false));
  EventBus.on(Events.ZIPCODE_UPDATED, () => loadDeliveryEstimates());
};

export default initMadeDates;
